import type { PayloadAction } from "@reduxjs/toolkit";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import type { IReduxRequestNode } from "@litres-libs/types";
import { Request } from "../../request/Request";
import type { RootState } from "../../store";
import { generateId, getFormattedTime } from "./helpers";
import { addHydrateReducer } from "../../utils/hydrateReducer";

/**
 * - id* – ID заметки;
 * - group* – группа (см. параметр group выше);
 * - last_update* – время последней модификации записи;
 * - file* – номер трека-главы соответствующей аудиокниги;
 * - filename* – название трека-главы соответствующей аудиокниги;
 * - time_start* – начальное время позиции в секундах соответствующего трека-главы. *Пока для закладки используем только этот параметр;
 * - time_end* – конечное время позиции в секундах соответствующего трека-главы. *Этот параметр заложен для будущей реализации функционала выделения части трека «аудио цитаты»;
 * - percent* – процент прослушанного. Вычисляется на основе данных о номере главы аудио файла и времени, когда пользователь остановил прослушивание. Значение возвращается, если group = 0, в виде относительной величины, например, '53', означает, что аудиокнига прослушана на 53%;
 * - title – заголовок закладки, заданный пользователем.
 * */
interface INote {
  id: string;
  group: "0" | "1";
  last_update: string;
  file: string;
  filename: string;
  percent: string;
  time_start: string;
  time_end: string;
  title?: string;
}

interface IAudioNotes {
  my_notes?: INote[];
  lock_id?: string;
  art?: string;
  order: number;
}

interface IGetAudioNotesRequest {
  art: string;
  group?: "0" | "1";
}
interface IGetAudioNotesResponse {
  success: boolean;
  my_notes?: INote[];
}
export type TGetAudioNotesNode = IReduxRequestNode<
  IGetAudioNotesRequest,
  IGetAudioNotesResponse
>;
/**
 * Получение пользователем своих закладок на аудиокнигу
 * @param { number } art - ID аудиокниги.
 * @param { 0 | 1 | undefined } group - ID групп, закладки из которых нас интересуют. Если не передан – сервер вернёт все закладки. Принимает значения 0-1, имеются следующие «базовые» группу (классы выделения):
 * 0 – текущая позиция в треке-главе (в этом классе закладка всегда одна);
 * 1 – закладка.
 *
 * Возвращаемые значения
 * - my_notes – массив, содержащий список заметок/закладок данной книги
 * Если закладки для аудиокниги отсутствуют, то массив my_notes будет пустым.
 * */
export const getAudioNotes = createAsyncThunk(
  "audioNotes/getAudioNotes",
  async (params: { onSuccess?: (v: any) => void }, { getState }) => {
    const state = getState() as RootState;
    const { art = "" } = state.player.notes;
    const isGetNotesAvailable = !!art;
    if (!isGetNotesAvailable) {
      return;
    }
    const response = new Request.Cat2<
      IGetAudioNotesRequest,
      IGetAudioNotesResponse
    >({
      url: "/",
      provider: {
        func: "r_my_audio_notes",
        id: "my_notes",
      },
      request: {
        method: "POST",
        params: { art, group: "0" },
      },
      onSuccess: params.onSuccess,
    });

    return await response.fetch();
  }
);

interface IUpdateAudioNotesRequest {
  art: string;
  my_notes: INote[];
  lock_id: string;
}
/** Перезапись закладок аудиокниги
 * @param { number } art - ID аудиокниги, к которой относятся закладки;
 * @param { array } my_notes - массив закладок, которые следует перезаписать. По структуре соответствует ответу метода r_my_audio_notes.
 * Параметр time_end не будет обязательным, если передан параметр group=0 и будет приниматься равным time_start. Однако, если параметр time_end передан при group=0, то должно выполняться условие time_end=time_start;
 * @param { string } lock_id - ID блокировки, полученный в w_set_audio_bookmark_lock. Время блокировки – 20 минут.
 * */
export const updateAudioNotes = createAsyncThunk(
  "audioNotes/updateAudioNotes",
  async (params: null, { getState, dispatch }) => {
    const state = getState() as RootState;
    const { art = "", lock_id = "", my_notes = [] } = state.player.notes;
    const isUpdateNotesAvailable = art && lock_id;
    if (!isUpdateNotesAvailable) {
      return;
    }
    // у аудиокниг totalDuration - общая длительность аудиокниги, duration - длительность текущего трека
    // у подкастов totalDuration отсутствует, duration - длительность текущего трека (эпизода)
    const { playerData, playerSettings, currentTime } = state.player.data;
    const duration = playerData?.totalDuration || playerData?.duration;
    const currentTrackIndex = playerSettings.contentsData?.findIndex(
      item => item.url === playerSettings.url
    );
    const totalElapsed = playerData?.totalDuration
      ? (playerSettings.contentsData
          ?.slice(0, currentTrackIndex)
          .reduce((acc, item) => acc + item.duration, 0) || 0) + currentTime
      : currentTime;
    const percent = duration ? Math.round((totalElapsed / duration) * 100) : 0;
    const filename = (playerSettings.url || "").split("/").slice(-1)[0];

    const notes: INote[] = [
      {
        id:
          my_notes.length && my_notes[0].filename === filename
            ? my_notes[0].id
            : generateId(art, filename),
        group: "0",
        percent: percent.toFixed(),
        time_start: currentTime.toFixed(),
        time_end: currentTime.toFixed(),
        last_update: getFormattedTime(),
        file: String(state.player.notes.order),
        filename: filename,
      },
    ];
    const response = new Request.Cat2<IUpdateAudioNotesRequest, any>({
      url: "/",
      provider: {
        func: "w_replace_my_audio_notes",
        id: "w_replace_my_audio_notes",
      },
      request: {
        method: "POST",
        params: { art, my_notes: notes, lock_id },
      },
      onSuccess: () => {
        dispatch(setMyNotes(notes));
      },
    });

    return await response.fetch();
  }
);

interface ILockAudioNotesRequest {
  art: string;
}
/**
 * Запросить блокировку закладок аудиокниг
 * @param { number } art - ID книги, закладки по которой следует заблокировать.
 * */
export const lockAudioNotes = createAsyncThunk(
  "audioNotes/lockAudioNotes",
  async (params: null, { getState }) => {
    const state = getState() as RootState;
    const { art = "" } = state.player.notes;
    const isLockNotesAvailable = !!art;
    if (!isLockNotesAvailable) {
      return;
    }
    const response = new Request.Cat2<ILockAudioNotesRequest, any>({
      url: "/",
      provider: {
        func: "w_set_audio_bookmark_lock",
        id: "w_set_audio_bookmark_lock",
      },
      request: {
        method: "POST",
        params: { art },
      },
    });

    return await response.fetch();
  }
);

const initialState: IAudioNotes = {
  order: 0,
};

export const audioNotesSlice = createSlice({
  name: "audioNotes",
  initialState,
  reducers: {
    setArtId: (state: IAudioNotes, action: PayloadAction<string>) => {
      state.art = action.payload;
    },
    setMyNotes: (state: IAudioNotes, action: PayloadAction<INote[]>) => {
      state.my_notes = action.payload;
    },
    setOrder: (state: IAudioNotes, action: PayloadAction<number>) => {
      state.order = action.payload;
    },
  },
  extraReducers: builder => {
    addHydrateReducer(builder, "player.notes");

    builder.addCase(lockAudioNotes.fulfilled, (state, { payload }) => {
      if (payload?.response?.lock_id) {
        state.lock_id = payload.response.lock_id;
      }
    });
  },
});

export const { setArtId, setMyNotes, setOrder } = audioNotesSlice.actions;
export default audioNotesSlice.reducer;
